<template>
  <div class="ct-stsg-shell">
    <div class="ct-stsg-tabs" role="tablist" aria-label="Secciones STSG">
      <button
        type="button"
        class="ct-stsg-tab"
        :class="{ active: view === 'receptivo', pending: !seen.receptivo }"
        @click="view = 'receptivo'"
      >
        <span>Receptivo</span>
        <small>{{ tabHint("receptivo") }}</small>
      </button>
      <button
        type="button"
        class="ct-stsg-tab"
        :class="{ active: view === 'expresivo', pending: !seen.expresivo }"
        @click="view = 'expresivo'"
      >
        <span>Expresivo</span>
        <small>{{ tabHint("expresivo") }}</small>
      </button>
    </div>

    <div class="ct-stsg-section-title">
      <div>
        <h3>{{ view === "receptivo" ? "Receptivo" : "Expresivo" }}</h3>
        <p>
          {{ sectionErrorMarks(view) }} respuestas incorrectas marcadas ·
          {{ sectionItemsWithErrors(view) }} ítems con error
        </p>
      </div>
      <button
        class="ct-stsg-clear-btn"
        type="button"
        @click="clearSection(view)"
      >
        Limpiar sección
      </button>
    </div>

    <div class="ct-stsg-grid">
      <div
        v-for="item in activeItems"
        :key="`${view}-${item.id}`"
        class="ct-stsg-card stsg-card-compact"
        :class="{
          'has-errors': selectionMask(item.id) > 0,
          'ct-guide-blink': isGuideItem(item.id),
        }"
      >
        <div class="ct-stsg-card-head">
          <div class="ct-stsg-card-title">
            <span class="ct-stsg-badge">{{ item.id }}</span>
            <span>Ítem {{ item.id }}</span>
          </div>
          <span
            class="ct-stsg-state-pill"
            :class="selectionMask(item.id) > 0 ? 'bad' : 'ok'"
          >
            {{ itemStateLabel(item.id) }}
          </span>
        </div>
        <div class="ct-stsg-body stsg-body-compact">
          <label
            class="ct-stsg-option-line"
            :class="{ checked: isChecked(item.id, 1) }"
            title="Marca esta oración solo si fue incorrecta"
          >
            <input
              type="checkbox"
              :checked="isChecked(item.id, 1)"
              @mousedown="onOptionInteract"
              @focus="onOptionInteract"
              @change="setChoice(item.id, 1, $event.target.checked)"
            />
            <span class="line-text">
              <span class="mini-tag">A</span>
              {{ item.a }}
            </span>
            <span
              class="ct-stsg-state-label"
              :class="isChecked(item.id, 1) ? 'bad' : 'ok'"
            >
              {{ optionStateLabel(item.id, 1) }}
            </span>
          </label>
          <label
            class="ct-stsg-option-line"
            :class="{ checked: isChecked(item.id, 2) }"
            title="Marca esta oración solo si fue incorrecta"
          >
            <input
              type="checkbox"
              :checked="isChecked(item.id, 2)"
              @mousedown="onOptionInteract"
              @focus="onOptionInteract"
              @change="setChoice(item.id, 2, $event.target.checked)"
            />
            <span class="line-text">
              <span class="mini-tag">B</span>
              {{ item.b }}
            </span>
            <span
              class="ct-stsg-state-label"
              :class="isChecked(item.id, 2) ? 'bad' : 'ok'"
            >
              {{ optionStateLabel(item.id, 2) }}
            </span>
          </label>
        </div>
      </div>
    </div>

    <div v-if="corrected" class="ct-stsg-result ct-stsg-result-polished">
      <span>Edad: {{ ageLabel }}</span>
      <span>Receptivo: {{ receptivoScore }}/46</span>
      <span>Expresivo: {{ expresivoScore }}/46</span>
      <span>Errores: {{ totalErrorMarks }}</span>
    </div>

    <div
      v-if="showReviewModal"
      class="ct-stsg-modal-backdrop"
      @click.self="showReviewModal = false"
    >
      <div class="ct-stsg-modal" role="dialog" aria-modal="true">
        <h3>¿Revisaste ambas secciones?</h3>
        <p>
          Aún no revisaste
          <strong>{{ modalSectionsText() }}</strong>. Si ya la revisaste y está
          sin errores, puedes corregir; si te faltó, vuelve a revisarla antes de
          continuar.
        </p>
        <div class="ct-stsg-modal-actions">
          <button
            type="button"
            class="ct-stsg-modal-btn secondary"
            @click="goReviewPending"
          >
            Ir a revisar
          </button>
          <button
            type="button"
            class="ct-stsg-modal-btn"
            @click="confirmAndCorrect"
          >
            Ya revisé, corregir
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { STSG_DATA } from "../data/stsgData";
import { submitCorrection, createCorrectionPayload } from "@/services/testCorrectionAdapter";

function emptyMasks() {
  return { receptivo: Array(23).fill(0), expresivo: Array(23).fill(0) };
}

function bitCount(value) {
  const mask = Number(value || 0);
  return (mask & 1 ? 1 : 0) + (mask & 2 ? 1 : 0);
}

export default {
  name: "StsgForm",
  inject: {
    corregirTestWorkspace: { default: null },
  },
  props: {
    testValue: { type: Number, required: true },
    age: { type: String, default: "" },
    initialAnswers: { type: Object, default: null },
  },
  data() {
    return {
      view: "receptivo",
      masks: emptyMasks(),
      // Una sección se marca "vista" al abrir su pestaña o marcar algo en ella.
      seen: { receptivo: false, expresivo: false },
      showReviewModal: false,
      modalSections: [],
      corrected: false,
      loading: false,
    };
  },
  created() {
    this.hydrate(this.initialAnswers);
  },
  computed: {
    activeItems() {
      return STSG_DATA[this.view] || [];
    },
    ageLabel() {
      return this.age || "Sin seleccionar";
    },
    totalErrorMarks() {
      return (
        this.sectionErrorMarks("receptivo") +
        this.sectionErrorMarks("expresivo")
      );
    },
    receptivoScore() {
      return 46 - this.sectionErrorMarks("receptivo");
    },
    expresivoScore() {
      return 46 - this.sectionErrorMarks("expresivo");
    },
    toolbarLabel() {
      return "Corregir test";
    },
    toolbarDisabled() {
      if (this.loading || this.corrected) return true;
      return !this.age;
    },
  },
  watch: {
    toolbarLabel: { immediate: true, handler: "emitToolbarMeta" },
    toolbarDisabled: { immediate: true, handler: "emitToolbarMeta" },
    loading: { handler: "emitToolbarMeta" },
    view(section) {
      this.markSeen(section);
    },
  },
  methods: {
    markSeen(section) {
      if (!this.seen[section]) this.$set(this.seen, section, true);
    },
    tabHint(section) {
      if (!this.seen[section]) return "Por revisar";
      return `${this.sectionErrorMarks(section)} errores`;
    },
    unreviewedSections() {
      const out = [];
      if (!this.seen.receptivo) out.push("receptivo");
      if (!this.seen.expresivo) out.push("expresivo");
      return out;
    },
    sectionLabel(section) {
      return section === "receptivo" ? "Receptivo" : "Expresivo";
    },
    modalSectionsText() {
      return this.modalSections.map(this.sectionLabel).join(" y ");
    },
    goReviewPending() {
      const first = this.modalSections[0];
      this.showReviewModal = false;
      if (first) this.view = first;
    },
    confirmAndCorrect() {
      this.showReviewModal = false;
      this.runCorrection();
    },
    emitToolbarMeta() {
      this.$emit("toolbar-meta", {
        label: this.toolbarLabel,
        disabled: this.toolbarDisabled,
        loading: this.loading,
      });
    },
    sectionErrorMarks(section) {
      return this.masks[section].reduce((sum, value) => sum + bitCount(value), 0);
    },
    sectionItemsWithErrors(section) {
      return this.masks[section].filter((value) => Number(value || 0) > 0).length;
    },
    selectionMask(id) {
      return this.masks[this.view][id - 1] || 0;
    },
    itemStateLabel(id) {
      const count = bitCount(this.selectionMask(id));
      if (!count) return "sin errores";
      return `${count} error${count > 1 ? "es" : ""}`;
    },
    isChecked(id, option) {
      const mask = option === 1 ? 1 : 2;
      return Boolean(this.masks[this.view][id - 1] & mask);
    },
    optionStateLabel(id, option) {
      return this.isChecked(id, option) ? "Incorrecta" : "Correcta";
    },
    isGuideItem(id) {
      const ws = this.corregirTestWorkspace;
      return (
        ws &&
        this.view === "receptivo" &&
        id === 1 &&
        ws.isFieldGuideActive(1)
      );
    },
    onOptionInteract() {
      const ws = this.corregirTestWorkspace;
      if (ws && ws.dismissFieldGuide) ws.dismissFieldGuide();
    },
    setChoice(id, option, checked) {
      const bit = option === 1 ? 1 : 2;
      let current = this.masks[this.view][id - 1] || 0;
      current = checked ? current | bit : current & ~bit;
      this.$set(this.masks[this.view], id - 1, current);
      this.markSeen(this.view);
      this.corrected = false;
    },
    clearSection(section) {
      this.masks[section] = this.masks[section].map(() => 0);
      this.corrected = false;
      if (this.$toasted) {
        this.$toasted.show(`Se limpió la sección ${section}.`);
      }
    },
    buildPayload() {
      const answers = { edad: this.age };
      this.masks.receptivo.forEach((mask, idx) => {
        const id = idx + 1;
        if (mask & 1) answers[`r_${id}_1`] = true;
        if (mask & 2) answers[`r_${id}_2`] = true;
      });
      this.masks.expresivo.forEach((mask, idx) => {
        const id = idx + 1;
        if (mask & 1) answers[`e_${id}_1`] = true;
        if (mask & 2) answers[`e_${id}_2`] = true;
      });
      return answers;
    },
    // Inversa de buildPayload: solo las oraciones incorrectas viajan (y siempre
    // como true), así que una clave ausente significa "correcta" y basta con
    // encender el bit de las que están.
    hydrate(stored) {
      if (!stored) return;
      [
        ["receptivo", "r"],
        ["expresivo", "e"],
      ].forEach(([section, prefix]) => {
        this.masks[section].forEach((_mask, idx) => {
          const id = idx + 1;
          let value = 0;
          if (stored[`${prefix}_${id}_1`]) value |= 1;
          if (stored[`${prefix}_${id}_2`]) value |= 2;
          if (value) this.$set(this.masks[section], idx, value);
        });
      });
      // Ambas secciones ya fueron revisadas cuando se corrigió por primera vez;
      // sin esto el modal de "¿revisaste ambas?" saltaría en cada edición.
      this.seen = { receptivo: true, expresivo: true };
    },
    async submit() {
      if (this.toolbarDisabled) return;
      const pending = this.unreviewedSections();
      if (pending.length) {
        this.modalSections = pending;
        this.showReviewModal = true;
        return;
      }
      await this.runCorrection();
    },
    async runCorrection() {
      this.loading = true;
      try {
        const data = await submitCorrection(
          createCorrectionPayload({
            testValue: this.testValue,
            answers: this.buildPayload(),
          })
        );
        this.corrected = true;
        this.$emit("corrected", data);
      } catch (err) {
        const msg =
          (err && err.message) ||
          "No se pudo corregir STSG. Revisa los datos.";
        if (this.$toasted) this.$toasted.error(msg);
      } finally {
        this.loading = false;
      }
    },
    reset() {
      this.masks = emptyMasks();
      this.view = "receptivo";
      this.seen = { receptivo: false, expresivo: false };
      this.showReviewModal = false;
      this.modalSections = [];
      this.corrected = false;
      this.loading = false;
    },
  },
};
</script>
